import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, MapPin, RefreshCw, CheckCircle2, Clock, AlertTriangle, ExternalLink, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import AuthImage from './AuthImage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const istDateLabel = () =>
  new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });

const fmtIST = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};

const fmtHours = (h) => {
  if (h === null || h === undefined) return '—';
  const totalMin = Math.round(h * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return `${hh}h ${mm}m`;
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { cls: 'bg-gray-100 text-gray-700', label: 'Awaiting Approval' },
    approved: { cls: 'bg-green-100 text-green-700', label: '✅ Approved' },
    flagged: { cls: 'bg-amber-100 text-amber-800', label: '⚠️ Flagged' },
    auto_closed: { cls: 'bg-gray-200 text-gray-600', label: 'Auto-closed' },
  };
  const m = map[status] || map.pending;
  return <span data-testid="att-status-badge" className={`text-xs font-semibold px-2 py-1 rounded-full ${m.cls}`}>{m.label}</span>;
};

// ---- Capture flow component (used for both check-in and check-out) ----
const CaptureFlow = ({ kind, onSubmit, submitting }) => {
  const [step, setStep] = useState('idle'); // idle | locating | located | error
  const [coords, setCoords] = useState(null);
  const [accuracyWarn, setAccuracyWarn] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const requestLocation = useCallback(() => {
    setStep('locating');
    setErrorMsg('');
    setAccuracyWarn(false);
    if (!navigator.geolocation) {
      setStep('error');
      setErrorMsg("Your device doesn't support location. Can't record attendance here.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({ latitude, longitude, accuracy });
        if (accuracy && accuracy > 200) setAccuracyWarn(true);
        setStep('located');
        // open camera right after location captured
        setTimeout(() => fileInputRef.current?.click(), 150);
      },
      (err) => {
        setStep('error');
        if (err.code === err.PERMISSION_DENIED) {
          setErrorMsg('Location access is required. Enable location for this site in your browser settings, then tap Retry.');
        } else {
          setErrorMsg("Couldn't get your location. Make sure GPS is on and tap Retry.");
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const retake = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    fileInputRef.current?.click();
  };

  const confirm = () => {
    if (!coords || !photoFile) return;
    onSubmit({ coords, photoFile });
  };

  const isCheckIn = kind === 'check-in';
  const accent = isCheckIn ? 'green' : 'blue';

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        data-testid={`att-${kind}-file-input`}
      />

      {step === 'idle' && (
        <Button
          data-testid={`att-${kind}-start-btn`}
          onClick={requestLocation}
          className={`w-full h-12 text-base ${isCheckIn ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          <Camera className="h-5 w-5 mr-2" /> {isCheckIn ? 'Check In' : 'Check Out'}
        </Button>
      )}

      {step === 'locating' && (
        <div data-testid="att-locating" className="flex items-center justify-center gap-2 py-4 text-gray-600 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Getting your location…
        </div>
      )}

      {step === 'error' && (
        <div data-testid="att-location-error" className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
          <div className="flex items-start gap-2 text-red-700 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{errorMsg}</span>
          </div>
          <Button data-testid={`att-${kind}-retry-btn`} onClick={requestLocation} variant="outline" size="sm" className="w-full">
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      )}

      {step === 'located' && coords && (
        <div className="space-y-3">
          <div data-testid="att-location-captured" className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <MapPin className="h-4 w-4" />
            <span>Location captured · {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}</span>
          </div>
          {accuracyWarn && (
            <div data-testid="att-accuracy-warn" className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4" /> Location accuracy is low — move to an open area if possible.
            </div>
          )}

          {photoPreview ? (
            <>
              <img data-testid={`att-${kind}-preview`} src={photoPreview} alt="preview" className="w-full rounded-lg border object-cover max-h-72" />
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid={`att-${kind}-confirm-btn`}
                  onClick={confirm}
                  disabled={submitting}
                  className={`h-11 ${isCheckIn ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm {isCheckIn ? 'Check In' : 'Check Out'}</>}
                </Button>
                <Button data-testid={`att-${kind}-retake-btn`} onClick={retake} variant="outline" disabled={submitting} className="h-11">
                  <RefreshCw className="h-4 w-4 mr-2" /> Retake
                </Button>
              </div>
            </>
          ) : (
            <Button data-testid={`att-${kind}-open-camera-btn`} onClick={() => fileInputRef.current?.click()} variant="outline" className="w-full h-11">
              <Camera className="h-4 w-4 mr-2" /> Open Camera
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---- History row ----
const HistoryRow = ({ rec }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`att-history-row-${rec.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50">
        <div className="flex-1 grid grid-cols-4 gap-2 text-sm items-center">
          <span className="font-medium">{rec.date}</span>
          <span className="text-gray-600">{fmtIST(rec.check_in?.time)}</span>
          <span className="text-gray-600">{fmtIST(rec.check_out?.time)}</span>
          <span className="text-gray-800">{fmtHours(rec.hours_worked)}</span>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <StatusBadge status={rec.status} />
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t bg-gray-50 grid grid-cols-2 gap-3">
          {['check_in', 'check_out'].map((k) => {
            const b = rec[k];
            const label = k === 'check_in' ? 'Check In' : 'Check Out';
            if (!b) return <div key={k} className="text-xs text-gray-400">{label}: {rec.status === 'auto_closed' ? 'Auto-closed' : 'Not recorded'}</div>;
            return (
              <div key={k} className="space-y-1">
                <p className="text-xs font-semibold text-gray-700">{label} · {fmtIST(b.time)}</p>
                <AuthImage path={b.photo_url} alt={label} className="w-full h-28 object-cover rounded border" />
                <a href={b.maps_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> View on Maps
                </a>
              </div>
            );
          })}
          {rec.status === 'flagged' && rec.admin_note && (
            <div className="col-span-2 text-xs bg-amber-50 text-amber-800 rounded p-2">Note from admin: {rec.admin_note}</div>
          )}
        </div>
      )}
    </div>
  );
};

const VEAttendance = () => {
  const { user, facility } = useAuth();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [liveSeconds, setLiveSeconds] = useState(0);
  const [enlarge, setEnlarge] = useState(null);

  const [historyMonth, setHistoryMonth] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7));
  const [history, setHistory] = useState(null);

  const loadToday = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/attendance/today`, { headers: authHeaders() });
      const data = await resp.json();
      setToday(data);
    } catch (e) {
      console.error('load today failed', e);
      toast.error('Could not load attendance status');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (month) => {
    try {
      const resp = await fetch(`${API_URL}/api/attendance/my-records?month=${month}`, { headers: authHeaders() });
      const data = await resp.json();
      setHistory(data);
    } catch (e) {
      console.error('load history failed', e);
    }
  }, []);

  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => { loadHistory(historyMonth); }, [historyMonth, loadHistory]);

  // live counter while checked in
  useEffect(() => {
    if (today?.status !== 'checked_in') return;
    const start = new Date(today.check_in_time).getTime();
    const tick = () => setLiveSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [today]);

  const submit = async (kind, { coords, photoFile }) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('photo', photoFile);
      fd.append('latitude', coords.latitude);
      fd.append('longitude', coords.longitude);
      fd.append('accuracy', coords.accuracy ?? '');
      const resp = await fetch(`${API_URL}/api/attendance/${kind}`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Something went wrong. Please try again.');
        return;
      }
      toast.success(kind === 'check-in' ? 'Checked in!' : 'Checked out!');
      await loadToday();
      await loadHistory(historyMonth);
    } catch (e) {
      console.error(`${kind} failed`, e);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const liveLabel = () => {
    const h = Math.floor(liveSeconds / 3600);
    const m = Math.floor((liveSeconds % 3600) / 60);
    const s = liveSeconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-4 pb-24 lg:pb-8" data-testid="ve-attendance-page">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Attendance — {istDateLabel()}</h1>
        <p className="text-sm text-gray-500">{facility?.name || user?.facility_name}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          {/* STATE 1: not checked in */}
          {today?.status === 'not_checked_in' && (
            <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3" data-testid="att-state-not-checked-in">
              <p className="font-semibold text-gray-800">Not checked in yet</p>
              <p className="text-sm text-gray-500">Take a photo at the venue. Your location is recorded automatically.</p>
              <CaptureFlow kind="check-in" onSubmit={(p) => submit('check-in', p)} submitting={submitting} />
            </div>
          )}

          {/* STATE 2: checked in, not out */}
          {today?.status === 'checked_in' && (
            <div className="space-y-4" data-testid="att-state-checked-in">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-700 font-semibold">
                  <CheckCircle2 className="h-5 w-5" /> Checked in at {fmtIST(today.check_in_time)}
                </div>
                <AuthImage
                  path={today.check_in_photo_url}
                  alt="check in"
                  className="w-28 h-28 object-cover rounded-lg border cursor-pointer"
                  onClick={() => setEnlarge(today.check_in_photo_url)}
                />
                <a href={today.check_in_maps_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> View on Maps <ExternalLink className="h-3 w-3" />
                </a>
                <div data-testid="att-live-counter" className="flex items-center gap-2 text-gray-800 font-medium">
                  <Clock className="h-4 w-4" /> Time at venue: {liveLabel()}
                </div>
              </div>
              <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3">
                <p className="font-semibold text-gray-800">Ready to leave?</p>
                <CaptureFlow kind="check-out" onSubmit={(p) => submit('check-out', p)} submitting={submitting} />
              </div>
            </div>
          )}

          {/* STATE 3: completed */}
          {today?.status === 'completed' && (
            <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3" data-testid="att-state-completed">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-800">Today's Attendance</p>
                <StatusBadge status={today.attendance_status} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Check In', time: today.check_in_time, photo: today.check_in_photo_url, maps: today.check_in_maps_url },
                  { label: 'Check Out', time: today.check_out_time, photo: today.check_out_photo_url, maps: today.check_out_maps_url },
                ].map((b) => (
                  <div key={b.label} className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-700">{b.label} · {fmtIST(b.time)}</p>
                    <AuthImage path={b.photo} alt={b.label} className="w-full h-32 object-cover rounded-lg border cursor-pointer" onClick={() => setEnlarge(b.photo)} />
                    <a href={b.maps} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> View on Maps
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium text-gray-800">Hours worked: {fmtHours(today.hours_worked)}</p>
              {today.attendance_status === 'flagged' && today.admin_note && (
                <div className="text-xs bg-amber-50 text-amber-800 rounded p-2">Note from admin: {today.admin_note}</div>
              )}
              <p className="text-sm text-gray-500">Your attendance for today has been submitted.</p>
            </div>
          )}
        </>
      )}

      {/* MY HISTORY */}
      <div className="mt-6" data-testid="att-history-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-gray-800">My Attendance History</h2>
          <input
            type="month"
            data-testid="att-history-month"
            value={historyMonth}
            onChange={(e) => setHistoryMonth(e.target.value)}
            className="text-sm border rounded px-2 py-1"
          />
        </div>
        {history && (
          <p className="text-sm text-gray-500 mb-2">
            {history.summary.days_present} days · {fmtHours(history.summary.total_hours)} total · {history.summary.pending} pending
          </p>
        )}
        <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-gray-400 px-3 mb-1">
          <span>Date</span><span>In</span><span>Out</span><span>Hours</span>
        </div>
        <div className="space-y-2">
          {history?.records?.length ? (
            history.records.map((r) => <HistoryRow key={r.id} rec={r} />)
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">No records this month.</p>
          )}
        </div>
      </div>

      {/* Enlarge modal */}
      {enlarge && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEnlarge(null)} data-testid="att-photo-modal">
          <AuthImage path={enlarge} alt="enlarged" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default VEAttendance;
